import axios from 'axios'
import sodium from 'libsodium-wrappers'

import {
    COINBASE_API_HOST,
    // This is a JSON downloaded from the CB dev portal.
    // Looks like {"id":"e0aef84e-...","privateKey":"fXWw..."}
    COINBASE_SECRET_EDDSA,
    // This is "organizations/9bcdc493-be30-4892-b70a-0ed8343a0eca/apiKeys/"
    // This is extracted from the ecdsa keys, because eddsa keys don't have it.
    COINBASE_SECRET_ORG_PREFIX,
} from '@src/pages/FiatExchange/config'
import {
    BuyConfigResponse,
    BuyOptionsResponse,
    BuyQuoteRequest,
    BuyQuoteResponse,
    SellConfigResponse,
    SellOptionsResponse,
    SellQuoteRequest,
    SellQuoteResponse,
    SessionTokenRequest,
    SessionTokenResponse,
} from '@src/types/coinbase'
import { createContextLogger } from '@src/utils'

const KEY_NAME: string = `${COINBASE_SECRET_EDDSA.id}`
const KEY_SECRET: string = COINBASE_SECRET_EDDSA.privateKey

const logger = createContextLogger('coinbase.api')

logger.info(`COINBASE_API_HOST =`, COINBASE_API_HOST)
logger.info(`KEY_NAME =`, KEY_NAME)

/**
 * Generates a JSON Web Token (JWT)
 */
async function getJWT(request_method: 'GET' | 'POST', request_path: string): Promise<string> {
    await sodium.ready
    const now: number = Math.floor(Date.now() / 1000)
    const payload = {
        iss: 'cdp',
        nbf: now,
        exp: now + 120,
        sub: KEY_NAME,
        uri: `${request_method} ${COINBASE_API_HOST}${request_path}`,
    }

    const { headerAndPayloadBase64URL, keyBuf } = encode(payload, KEY_SECRET, 'EdDSA')

    // Convert the header and payload string into a Uint8Array for signing
    const encoder = new TextEncoder()
    const dataToSign: Uint8Array = encoder.encode(headerAndPayloadBase64URL)

    // Create the signature using libsodium
    const signature: Uint8Array = sodium.crypto_sign_detached(dataToSign, keyBuf)

    // Convert the signature to a base64url string using our helper
    const signatureBase64url: string = uint8ArrayToBase64Url(signature)

    const jwt: string = `${headerAndPayloadBase64URL}.${signatureBase64url}`
    return jwt
}

const axiosInstance = axios.create({
    baseURL: `https://${COINBASE_API_HOST}`,
    headers: {
        'Content-Type': 'application/json',
    },
})

/**
 * cbGetSessionToken
 * POST /onramp/v1/token
 * Example payload:
 * {
 *   addresses: [{ address: '0x4315d134aCd3221a02dD380ADE3aF39Ce219037c', blockchains: ['ethereum', 'base'] }],
 *   assets: ['ETH', 'USDC']
 * }
 */
export const cbGetSessionToken = async (payload: SessionTokenRequest): Promise<SessionTokenResponse> => {
    const jwt = await getJWT('POST', '/onramp/v1/token')
    const response = await axiosInstance({
        method: 'POST',
        url: '/onramp/v1/token',
        data: payload,
        headers: {
            Authorization: `Bearer ${jwt}`,
        },
    })
    return response.data
}

/**
 * cbGetOnrampConfig
 * GET /onramp/v1/buy/config
 */
export const cbGetOnrampConfig = async (): Promise<BuyConfigResponse[]> => {
    const jwt = await getJWT('GET', '/onramp/v1/buy/config')
    const response = await axiosInstance({
        method: 'GET',
        url: '/onramp/v1/buy/config',
        headers: {
            Authorization: `Bearer ${jwt}`,
        },
    })
    return response.data
}

/**
 * cbGetOnrampOptions
 * GET /onramp/v1/buy/options
 * @param country - Country code in ISO 3166-1 format.
 */
export const cbGetOnrampOptions = async (country: string): Promise<BuyOptionsResponse> => {
    const jwt = await getJWT('GET', '/onramp/v1/buy/options')
    const response = await axiosInstance({
        method: 'GET',
        url: '/onramp/v1/buy/options',
        params: { country },
        headers: {
            Authorization: `Bearer ${jwt}`,
        },
    })
    return response.data
}

/**
 * cbGetOnrampQuote
 * POST /onramp/v1/buy/quote
 * @param quoteRequest - Request payload of type BuyQuoteRequest.
 * @returns Promise resolving to a BuyQuoteResponse.
 */
export const cbGetOnrampQuote = async (quoteRequest: BuyQuoteRequest): Promise<BuyQuoteResponse> => {
    const jwt = await getJWT('POST', '/onramp/v1/buy/quote')
    const response = await axiosInstance({
        method: 'POST',
        url: '/onramp/v1/buy/quote',
        data: quoteRequest,
        headers: {
            Authorization: `Bearer ${jwt}`,
        },
    })
    return response.data
}

/**
 * cbGetOfframpConfig
 * GET /onramp/v1/sell/config
 */
export const cbGetOfframpConfig = async (): Promise<SellConfigResponse[]> => {
    const jwt = await getJWT('GET', '/onramp/v1/sell/config')
    const response = await axiosInstance({
        method: 'GET',
        url: '/onramp/v1/sell/config',
        headers: {
            Authorization: `Bearer ${jwt}`,
        },
    })
    return response.data
}

/**
 * cbGetOfframpOptions
 * GET /onramp/v1/sell/options
 * @param country - Country code in ISO 3166-1 format.
 * @param subdivision - Optional subdivision code.
 */
export const cbGetOfframpOptions = async (country: string, subdivision?: string): Promise<SellOptionsResponse> => {
    const jwt = await getJWT('GET', '/onramp/v1/sell/options')

    // Build query parameters
    const params: Record<string, string> = { country }
    if (subdivision) {
        params.subdivision = subdivision
    }

    const response = await axiosInstance({
        method: 'GET',
        url: '/onramp/v1/sell/options',
        params,
        headers: {
            Authorization: `Bearer ${jwt}`,
        },
    })
    return response.data
}

/**
 * cbGetOfframpQuote
 * POST /onramp/v1/sell/quote
 * @param quoteRequest - Request payload of type SellQuoteRequest.
 * @returns Promise resolving to a SellQuoteResponse.
 */
export const cbGetOfframpQuote = async (quoteRequest: SellQuoteRequest): Promise<SellQuoteResponse> => {
    const jwt = await getJWT('POST', '/onramp/v1/sell/quote')
    const response = await axiosInstance({
        method: 'POST',
        url: '/onramp/v1/sell/quote',
        data: quoteRequest,
        headers: {
            Authorization: `Bearer ${jwt}`,
        },
    })
    return response.data
}

/**
 * Encodes the header and payload as a base64url string and converts the key from base64 to Uint8Array.
 */
function encode(payload: any, key: string, alg: string): { headerAndPayloadBase64URL: string; keyBuf: Uint8Array } {
    const header = {
        typ: 'JWT',
        alg,
        kid: KEY_NAME,
        nonce: getRandomHexString(16),
    }

    // JSON-stringify header and payload, then encode them to base64url strings
    const headerBase64URL: string = uint8ArrayToBase64Url(new TextEncoder().encode(JSON.stringify(header)))
    const payloadBase64URL: string = uint8ArrayToBase64Url(new TextEncoder().encode(JSON.stringify(payload)))
    const headerAndPayloadBase64URL: string = `${headerBase64URL}.${payloadBase64URL}`

    // Convert the provided key (base64 string) to a Uint8Array
    const keyBuf: Uint8Array = base64ToUint8Array(key)
    return { headerAndPayloadBase64URL, keyBuf }
}

/**
 * Generates a random hexadecimal string of the given byte length.
 */
function getRandomHexString(byteLength: number): string {
    const randomBytes = new Uint8Array(byteLength)
    window.crypto.getRandomValues(randomBytes)
    return Array.from(randomBytes)
        .map(byte => byte.toString(16).padStart(2, '0'))
        .join('')
}

/**
 * Converts a base64 string to a Uint8Array.
 */
function base64ToUint8Array(b64: string): Uint8Array {
    const binaryString = window.atob(b64)
    const len = binaryString.length
    const bytes = new Uint8Array(len)
    for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i)
    }
    return bytes
}

/**
 * Converts a Uint8Array into a base64url encoded string.
 */
function uint8ArrayToBase64Url(uint8: Uint8Array): string {
    let binary = ''
    for (let i = 0; i < uint8.length; i++) {
        binary += String.fromCharCode(uint8[i])
    }
    const base64 = window.btoa(binary)
    // Convert to base64url format by replacing characters and trimming padding
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}
